import { memo, useMemo, useRef, useState, CSSProperties } from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ReferenceLine,
} from "recharts";
import useDeepCompareEffect from "../../hooks/useDeepCompareEffect";
import {
    DisaggregatedMetric,
    GeoDetails,
    GeoTag,
    MetricMetadata,
} from "../../services/types";
import {
    calculateGeoPrefix,
    fetchMetricMetadata,
    fetchNationalData,
    fetchStateData,
    getGeoPrefix,
} from "../../utils/dashboardUtils";
import useFetchMetric from "../../hooks/useFetchMetric";
import MetricMetadataModal from "../MetricMetadataModal";
import metricMetadataIcon from "../../assets/metric-metadata-icon.svg";
import { useDashboardColor } from "../../context/DashboardColorContext";
import DefaultIcon from "../../assets/svgr/default-metric.svg?react";
import GradientBar from "../GradientBar";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

interface UnemploymentRateChartProps {
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    title?: string;
    height: string;
    lineColor?: string;
    svg?: React.FC<React.SVGProps<SVGSVGElement>> | null;
    showMetadataIcon?: boolean;
    subText?: string;
    titleOverride?: string;
}

type ChartPoint = {
    label: string;
    selected: number | null;
    state: number | null;
    national: number | null;
    selectedRaw: number | string | null;
    stateRaw: number | string | null;
    nationalRaw: number | string | null;
};

const METRIC_ID = 1116;
const SELECTED_COLOR = "#9D1E27";
const STATE_COLOR = "#C1BB00";
const NATIONAL_COLOR = "#125687";

const normalizeSeries = (metricData: DisaggregatedMetric[] | null) => {
    if (!metricData?.length) {
        return {
            points: [] as { label: string; value: number; rawValue: number | string }[],
            versionValue: null as number | null,
        };
    }

    const indicatorData = metricData[0];
    const version = indicatorData?.versions?.[0];

    if (!version?.disaggregations?.length) {
        return {
            points: [] as { label: string; value: number; rawValue: number | string }[],
            versionValue: null as number | null,
        };
    }

    const tallyMap = new Map<string, number>();
    const rawValueMap = new Map<string, number | string>();

    version.disaggregations.forEach((d) => {
        let percentage = 0;
        let rawValue: number | string = "0";

        switch (version.unit) {
            case "integer":
                percentage = version.value ? d.value / version.value : 0;
                rawValue = d.value;
                break;
            case "percentage":
                percentage = Math.min(d.value, 1);
                rawValue = d.value;
                break;
            case "decimal":
            case "ratio":
                percentage = d.value;
                rawValue = d.value;
                break;
            default:
                percentage = 0;
                rawValue = "0";
        }

        tallyMap.set(d.name.trim(), percentage);
        rawValueMap.set(d.name.trim(), rawValue);
    });

    const points = Array.from(tallyMap.entries()).map(([name, percentage]) => ({
        label: name,
        value: percentage,
        rawValue: rawValueMap.get(name) ?? 0,
    }));

    return {
        points,
        versionValue: typeof version.value === "number" ? version.value : null,
    };
};

const percentText = (value: number | null | undefined) =>
    value == null ? "—" : `${(value * 100).toFixed(1)}%`;

const isFiniteCoord = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

const SelectedDot = (props: any) => {
    const { cx, cy } = props;

    if (!isFiniteCoord(cx) || !isFiniteCoord(cy)) return null;

    return <rect x={cx - 5} y={cy - 5} width={10} height={10} fill={SELECTED_COLOR} />;
};

const StateDot = (props: any) => {
    const { cx, cy } = props;

    if (!isFiniteCoord(cx) || !isFiniteCoord(cy)) return null;

    return (
        <path
            d={`M ${cx} ${cy - 6} L ${cx - 6} ${cy + 5} L ${cx + 6} ${cy + 5} Z`}
            fill={STATE_COLOR}
        />
    );
};

const NationalDot = (props: any) => {
    const { cx, cy } = props;

    if (!isFiniteCoord(cx) || !isFiniteCoord(cy)) return null;

    return <circle cx={cx} cy={cy} r={5} fill={NATIONAL_COLOR} />;
};

const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    return (
        <div className="bg-white p-2 border border-slate-200 rounded shadow-sm text-xs">
            <p className="font-semibold">{label}</p>
            {payload.map((entry: any) => {
                const rawKey =
                    entry.dataKey === "selected"
                        ? "selectedRaw"
                        : entry.dataKey === "state"
                            ? "stateRaw"
                            : "nationalRaw";

                const rawValue = entry.payload?.[rawKey];
                const displayValue =
                    typeof rawValue === "number"
                        ? `${(rawValue * 100).toFixed(1)}%`
                        : rawValue ?? "—";

                return (
                    <p key={entry.dataKey}>
                        {entry.name}: {displayValue}
                    </p>
                );
            })}
        </div>
    );
};

const CustomLegend = ({
    selectedValue,
    stateValue,
    nationalValue,
}: {
    selectedValue: number | null;
    stateValue: number | null;
    nationalValue: number | null;
}) => {
    return (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-2 text-xs font-bold text-color-cai-brown">
            <div className="flex items-center gap-2">
                <span className="inline-block w-[10px] h-[10px] bg-[#9D1E27]" />
                <span>Selected Tracts: {percentText(selectedValue)}</span>
            </div>

            <div className="flex items-center gap-2">
                <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M6 1 L1 10 L11 10 Z" fill={STATE_COLOR} />
                </svg>
                <span>State: {percentText(stateValue)}</span>
            </div>

            <div className="flex items-center gap-2">
                <span className="inline-block w-[10px] h-[10px] rounded-full bg-[#125687]" />
                <span>Country: {percentText(nationalValue)}</span>
            </div>
        </div>
    );
};

const UnemploymentRateChart = memo(
    ({
        geoDetails,
        geoTag,
        title = "Unemployment Rate",
        height,
        lineColor = "#E8E8E8",
        svg,
        showMetadataIcon = true,
        subText,
        titleOverride,
    }: UnemploymentRateChartProps) => {
        const color = useDashboardColor();
        const IconComponent = svg || DefaultIcon;

        const [stateMetricData, setStateMetricData] = useState<DisaggregatedMetric[] | null>(null);
        const [nationalMetricData, setNationalMetricData] = useState<DisaggregatedMetric[] | null>(null);

        const [showMetadata, setShowMetadata] = useState(false);
        const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState<Error | null>(null);

        const triggerRef = useRef<HTMLButtonElement | null>(null);
        const modalId = `metric-metadata-modal-${METRIC_ID}`;
        const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

        const geoPrefix = geoTag?.value
            ? getGeoPrefix(geoTag.value)
            : calculateGeoPrefix(geoDetails);

        const { metricData } = useFetchMetric(geoDetails, METRIC_ID, geoTag);

        function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
            if (fetchCache.current[key]) {
                return fetchCache.current[key] as Promise<T>;
            }

            const promise = fetchFn().finally(() => {
                delete fetchCache.current[key];
            });

            fetchCache.current[key] = promise;
            return promise;
        }

        useDeepCompareEffect(() => {
            const fetchComparisons = async () => {
                try {
                    if (!stateMetricData && geoPrefix) {
                        const stateRes = await fetchStateData(METRIC_ID, true, geoPrefix);
                        setStateMetricData(stateRes?.data ?? null);
                    }

                    if (!nationalMetricData) {
                        const nationalRes = await fetchNationalData(METRIC_ID, true);
                        setNationalMetricData(nationalRes?.data ?? null);
                    }
                } catch (err) {
                    console.error(err);
                }
            };

            fetchComparisons();
        }, [geoPrefix]);

        const selectedSeries = useMemo(() => normalizeSeries(metricData), [metricData]);
        const stateSeries = useMemo(() => normalizeSeries(stateMetricData), [stateMetricData]);
        const nationalSeries = useMemo(() => normalizeSeries(nationalMetricData), [nationalMetricData]);

        const chartData = useMemo<ChartPoint[]>(() => {
            const labels =
                selectedSeries.points.length > 0
                    ? selectedSeries.points.map((d) => d.label)
                    : stateSeries.points.length > 0
                        ? stateSeries.points.map((d) => d.label)
                        : nationalSeries.points.map((d) => d.label);

            return labels.map((label) => {
                const selectedPoint = selectedSeries.points.find((d) => d.label === label);
                const statePoint = stateSeries.points.find((d) => d.label === label);
                const nationalPoint = nationalSeries.points.find((d) => d.label === label);

                return {
                    label,
                    selected: selectedPoint?.value ?? null,
                    state: statePoint?.value ?? null,
                    national: nationalPoint?.value ?? null,
                    selectedRaw: selectedPoint?.rawValue ?? null,
                    stateRaw: statePoint?.rawValue ?? null,
                    nationalRaw: nationalPoint?.rawValue ?? null,
                };
            });
        }, [selectedSeries, stateSeries, nationalSeries]);

        const lastLabel = chartData[chartData.length - 1]?.label;

        const getNumericValues = (data: ChartPoint[]) =>
            data.flatMap((d) => [d.selected, d.state, d.national]).filter((v): v is number => typeof v === "number");

        const numericValues = useMemo(() => getNumericValues(chartData), [chartData]);

        const yDomain = useMemo<[number, number]>(() => {
            if (!numericValues.length) return [0, 1];

            const min = Math.min(...numericValues);
            const max = Math.max(...numericValues);

            if (min === max) {
                const pad = Math.max(min * 0.15, 0.01);
                return [Math.max(0, min - pad), max + pad];
            }

            const range = max - min;
            const pad = Math.max(range * 0.35, 0.01);

            return [Math.max(0, min - pad), max + pad];
        }, [numericValues]);

        const handleClick = async () => {
            setShowMetadata((prev) => !prev);

            const fetchPromises: Promise<void>[] = [];

            if (!metadata) {
                fetchPromises.push(
                    fetchWithCache(`metadata-${METRIC_ID}`, () => fetchMetricMetadata(METRIC_ID)).then((res) => {
                        setMetadata(res?.data ?? null);
                    })
                );
            }

            if (!stateMetricData && geoPrefix) {
                fetchPromises.push(
                    fetchWithCache(`stateData-${METRIC_ID}-${geoPrefix}`, () =>
                        fetchStateData(METRIC_ID, true, geoPrefix)
                    ).then((res) => {
                        setStateMetricData(res?.data ?? null);
                    })
                );
            }

            if (!nationalMetricData) {
                fetchPromises.push(
                    fetchWithCache(`nationalData-${METRIC_ID}`, () =>
                        fetchNationalData(METRIC_ID, true)
                    ).then((res) => {
                        setNationalMetricData(res?.data ?? null);
                    })
                );
            }

            if (fetchPromises.length > 0) {
                setLoading(true);
                try {
                    await Promise.all(fetchPromises);
                } catch (err) {
                    const errorMessage =
                        (err as Error).message || "An unexpected error occurred.";
                    setError(new Error(`Failed to load data: ${errorMessage}`));
                } finally {
                    setLoading(false);
                }
            }
        };

        if (!metricData && !stateMetricData && !nationalMetricData) {
            return (
                <div className="h-24 flex items-center justify-center text-xs text-slate-500">
                    <Skeleton height={24} width={"100%"} />
                </div>
            );
        }

        if (!chartData.length) {
            return (
                <div className="h-24 flex items-center justify-center text-xs text-slate-500">
                    No data available
                </div>
            );
        }

        return (
            <>
                <div className="relative overflow-hidden group hover:shadow-md transition-all border rounded-xl bg-white">
                    <div className="p-3 bg-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-2">
                                    {svg && (
                                        <div
                                            className="inline-flex flex-shrink-0 rounded-lg p-1.5 transition-colors bg-[var(--bg)] group-hover:bg-[var(--bg-hover)]"
                                            style={
                                                {
                                                    "--bg": `${color}20`,
                                                    "--bg-hover": `${color}40`,
                                                } as CSSProperties
                                            }
                                        >
                                            <IconComponent
                                                className="w-8 h-8"
                                                style={{ color }}
                                                aria-label={`${title} icon`}
                                                role="img"
                                            />
                                        </div>
                                    )}

                                    <h3 className="text-sm font-semibold text-color-cai-brown">
                                        {titleOverride ?? title}
                                    </h3>
                                </div>

                                {subText && (
                                    <div className="flex items-center">
                                        <p className="text-color-cai-brown text-xs mr-2">{subText}</p>
                                    </div>
                                )}
                            </div>

                            {showMetadataIcon && (
                                <button
                                    ref={triggerRef}
                                    type="button"
                                    onClick={handleClick}
                                    aria-haspopup="dialog"
                                    aria-expanded={showMetadata ? "true" : "false"}
                                    aria-controls={modalId}
                                    aria-label={`Additional information about ${title}`}
                                    className="
                                        inline-flex h-6 w-6 items-center justify-center rounded
                                        transition-transform hover:scale-105 focus:scale-105
                                        focus-visible:outline-none
                                        focus-visible:ring-2 focus-visible:ring-offset-2
                                        focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                                    "
                                >
                                    <img
                                        src={metricMetadataIcon}
                                        alt=""
                                        aria-hidden="true"
                                        className="h-3 w-3"
                                        draggable="false"
                                    />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={`${height} min-h-[220px] min-w-0 px-3 pb-2`}>
                        <div className="relative w-full h-full">
                            {lastLabel && (
                                <div
                                    className="absolute text-xs text-color-cai-brown pointer-events-none z-10"
                                    style={{
                                        top: 0,
                                        right: 0,
                                    }}
                                >
                                    {lastLabel}
                                </div>
                            )}
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={chartData}
                                    margin={{ top: 14, right: 12, left: 12, bottom: 28 }}
                                >
                                    <XAxis
                                        dataKey="label"
                                        tick={false}
                                        axisLine={false}
                                        tickLine={false}
                                        interval={0}
                                    />
                                    <YAxis
                                        domain={yDomain}
                                        tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                                        tick={{ fontSize: 10, fill: "#666" }}
                                        axisLine={false}
                                        tickLine={false}
                                        width={34}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend
                                        verticalAlign="bottom"
                                        align="left"
                                        height={36}
                                        content={
                                            <CustomLegend
                                                selectedValue={selectedSeries.versionValue}
                                                stateValue={stateSeries.versionValue}
                                                nationalValue={nationalSeries.versionValue}
                                            />
                                        }
                                    />

                                    {lastLabel && (
                                        <ReferenceLine
                                            x={lastLabel}
                                            stroke="#E8E8E8"
                                            strokeWidth={3}
                                            ifOverflow="extendDomain"
                                        />
                                    )}

                                    <Line
                                        type="monotone"
                                        dataKey="selected"
                                        name="Selected Tracts"
                                        stroke={lineColor}
                                        strokeWidth={3}
                                        dot={<SelectedDot />}
                                        activeDot={{ r: 6 }}
                                        connectNulls
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="state"
                                        name="State Data"
                                        stroke={lineColor}
                                        strokeWidth={3}
                                        dot={<StateDot />}
                                        activeDot={{ r: 6 }}
                                        connectNulls
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="national"
                                        name="National Data"
                                        stroke={lineColor}
                                        strokeWidth={3}
                                        dot={<NationalDot />}
                                        activeDot={{ r: 6 }}
                                        connectNulls
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <GradientBar color={color} />
                </div>

                <MetricMetadataModal
                    isOpen={showMetadata}
                    onClose={handleClick}
                    metadata={metadata}
                    disaggData={metricData}
                    stateData={stateMetricData}
                    nationalData={nationalMetricData}
                    loading={loading}
                    error={error}
                    triggerRef={triggerRef}
                    modalId={modalId}
                />
            </>
        );
    }
);

export default UnemploymentRateChart;